import { User, UserDetails } from "trimble-connect-sdk"
import fetch from "node-fetch"
import { ConnectProjectRegion } from "../types"

const ConnectApiBaseUrl = "https://app21.connect.trimble.com/tc/api/2.0"

export interface ConnectData {
  // TODO: Set region as required. And throw error earlier if it is not set
  region?: ConnectProjectRegion
  unitSystem?: string
  boundaryCoordinates?: string
  crsName?: string
  connectProject?: any
}

export interface ConnectUser {
  /** This is the Connect User ID, not TID ! */
  id: string
  firstName: string
  lastName: string
  tiduuid: string
  thumbnail: string
  email: string
  company: {
    id: string
    name: string
  }
  role: "ADMIN" | "USER"
  status?: "ACTIVE" | "REMOVED"
}

class ConnectApi {
  private baseUrl: string

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || ConnectApiBaseUrl
  }

  public async getUser(): Promise<UserDetails> {
    const init = this.requestInit()
    const user = await fetch(`${this.baseUrl}/users/me`, init)
      .then((res) => res.json())
      .then(async (data) => await fetch(`${this.baseUrl}/users/${data.id}`, init))
      .then((res) => res.json())
    return user
  }

  public async isAdminUserRole(userId: string, projectId: string): Promise<boolean> {
    const init = this.requestInit()

    const users = await fetch(`${this.baseUrl}/projects/${projectId}/users`, init).then((res) => res.json())

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return users.filter((user: User) => user.id === userId)[0].role! === "ADMIN"
  }

  public async getLoggedInUserProjectRoles(): Promise<any[]> {
    const init = this.requestInit()

    const project = await fetch(`${this.baseUrl}/projects/me`, init)
      .then((res) => res.json())
      .catch((err) => console.error(err))

    return project
  }

  public async getProject(projectId: string): Promise<any> {
    const init = this.requestInit()

    const project = await fetch(`${this.baseUrl}/projects/${projectId}`, init)
      .then((res) => res.json())
      .catch((err) => console.error(err))

    return project
  }

  public async getConnectProjectData(projectId: string): Promise<ConnectData> {
    const init = this.requestInit()

    // Fetch two endpoints concurrently
    const [project, settings] = await Promise.all([
      fetch(`${this.baseUrl}/projects/${projectId}`, init).then((r) => r.json()),
      fetch(`${this.baseUrl}/projects/${projectId}/settings`, init).then((r) => r.json())
    ])

    return {
      connectProject: project,
      region: project?.location,
      unitSystem: settings?.unitSettings?.unitsystem,
      boundaryCoordinates: project?.address?.geometry,
      crsName: project?.crs?.name
    }
  }

  public async getConnectProjectUsers(projectId: string): Promise<ConnectUser[]> {
    const init = this.requestInit()

    return await fetch(`${this.baseUrl}/projects/${projectId}/users`, init).then((res) => res.json())
  }

  public async getConnectProjectUserGroups(projectId: string): Promise<{ id: string; name: string }[]> {
    const init = this.requestInit()

    const groups = await fetch(`${this.baseUrl}/groups?projectId=${projectId}`, init).then((res) => res.json())
    return groups
  }

  public async getUsersInAGroup(groupId: string): Promise<{ id: string; tiduuid: string }[]> {
    const init = this.requestInit()

    const groupUsers = await fetch(`${this.baseUrl}/groups/${groupId}/users`, init).then((res) => res.json())
    return groupUsers
  }

  private requestInit(method?: string) {
    const accessToken = localStorage.getItem("tc-access-token")
    return { method: method || "GET", headers: { Authorization: "Bearer " + accessToken } }
  }

  public async updateBoundaryCoordinates(
    projectId: string,
    updatedBoundaryCoordinates: string
  ): Promise<{ address: { geometry: string } }> {
    const accessToken = localStorage.getItem("tc-access-token")
    const data = { address: { geometry: updatedBoundaryCoordinates } }
    const init = {
      method: "PATCH",
      headers: { Authorization: "Bearer " + accessToken, "Content-Type": "application/json" },
      body: JSON.stringify(data)
    }

    const updatedProjectDetails = await fetch(`${this.baseUrl}/projects/${projectId}`, init).then((res) => res.json())
    return {
      address: updatedProjectDetails.address
    }
  }
}

export default ConnectApi
